import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { TRANSLATION_PAIRS } from '../i18n/translationPairs';

const LanguageContext = createContext(null);

const LANGUAGE_STORAGE_KEY = 'agromart_language';
const SUPPORTED_LANGUAGES = ['tr', 'en'];
const TRANSLATABLE_ATTRIBUTES = ['placeholder', 'title', 'aria-label'];
const SKIP_TRANSLATION_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'CODE', 'PRE', 'SVG', 'PATH']);

const EN_TO_TR_PAIRS = [...TRANSLATION_PAIRS].sort((a, b) => b[0].length - a[0].length);
const TR_TO_EN_PAIRS = [...TRANSLATION_PAIRS]
  .map(([enText, trText]) => [trText, enText])
  .sort((a, b) => b[0].length - a[0].length);

const WORD_LEVEL_PAIRS = [
  ['Account', 'Hesap'],
  ['Settings', 'Ayarlar'],
  ['Profile', 'Profil'],
  ['Information', 'Bilgileri'],
  ['Manage', 'Yönet'],
  ['Update', 'Güncelle'],
  ['Activity', 'Etkinlik'],
  ['History', 'Geçmiş'],
  ['Search', 'Arama'],
  ['Saved', 'Kayıtlı'],
  ['Favorite', 'Favori'],
  ['Favorites', 'Favoriler'],
  ['Property', 'İlan'],
  ['Properties', 'İlanlar'],
  ['Listing', 'İlan'],
  ['Listings', 'İlanlar'],
  ['Add', 'Ekle'],
  ['Remove', 'Kaldır'],
  ['Delete', 'Sil'],
  ['Select', 'Seç'],
  ['Type', 'Tür'],
  ['Category', 'Kategori'],
  ['Price', 'Fiyat'],
  ['Status', 'Durum'],
  ['Address', 'Adres'],
  ['City', 'Şehir'],
  ['State', 'İl'],
  ['Country', 'Ülke'],
  ['Phone', 'Telefon'],
  ['Email', 'E-posta'],
  ['Message', 'Mesaj'],
  ['Details', 'Detaylar'],
  ['Description', 'Açıklama'],
  ['Payment', 'Ödeme'],
  ['Method', 'Yöntem'],
  ['Methods', 'Yöntemleri'],
  ['Subscription', 'Abonelik'],
  ['Subscriptions', 'Abonelikler'],
  ['Billing', 'Faturalama'],
  ['Card', 'Kart'],
  ['Number', 'Numara'],
  ['Name', 'Ad'],
  ['Date', 'Tarih'],
  ['Time', 'Saat'],
  ['Location', 'Konum'],
  ['Farm', 'Çiftlik'],
  ['Business', 'İşletme'],
  ['Certification', 'Sertifika'],
  ['License', 'Lisans'],
  ['Permit', 'İzin'],
  ['Visibility', 'Görünürlük'],
  ['Notification', 'Bildirim'],
  ['Notifications', 'Bildirimler'],
  ['Privacy', 'Gizlilik'],
  ['Security', 'Güvenlik'],
  ['Password', 'Şifre'],
  ['Current', 'Mevcut'],
  ['New', 'Yeni'],
  ['Confirm', 'Onayla'],
  ['Create', 'Oluştur'],
  ['Edit', 'Düzenle'],
  ['Export', 'Dışa Aktar'],
  ['Import', 'İçeri Aktar'],
  ['Analytics', 'Analitik'],
  ['Report', 'Rapor'],
  ['Reports', 'Raporlar'],
  ['Help', 'Yardım'],
  ['Support', 'Destek'],
  ['Loading', 'Yükleniyor'],
  ['Error', 'Hata'],
  ['Failed', 'Başarısız'],
  ['Success', 'Başarı'],
  ['Successfully', 'Başarıyla'],
  ['Required', 'Zorunlu'],
  ['Only', 'Sadece'],
  ['All', 'Tüm'],
  ['View', 'Görüntüle'],
  ['Show', 'Göster'],
  ['Share', 'Paylaş'],
  ['Contact', 'İletişim'],
  ['Online', 'Çevrimiçi'],
  ['Today', 'Bugün'],
  ['Month', 'Ay'],
  ['Year', 'Yıl'],
  ['Total', 'Toplam'],
  ['Revenue', 'Gelir'],
  ['Lead', 'Lead'],
  ['Leads', 'Leadler'],
];

const EN_TO_TR_WORD_PAIRS = [...WORD_LEVEL_PAIRS].sort((a, b) => b[0].length - a[0].length);
const TR_TO_EN_WORD_PAIRS = [...WORD_LEVEL_PAIRS]
  .map(([enText, trText]) => [trText, enText])
  .sort((a, b) => b[0].length - a[0].length);

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const applyWordLevelTranslation = (value, language) => {
  if (!value || typeof value !== 'string') {
    return value;
  }

  const wordPairs = language === 'tr' ? EN_TO_TR_WORD_PAIRS : TR_TO_EN_WORD_PAIRS;
  let translatedValue = value;

  wordPairs.forEach(([sourceWord, targetWord]) => {
    if (!sourceWord || !targetWord) {
      return;
    }

    const pattern = new RegExp(`\\b${escapeRegExp(sourceWord)}\\b`, 'gi');
    translatedValue = translatedValue.replace(pattern, (matchedWord) => {
      if (matchedWord === matchedWord.toUpperCase()) {
        return targetWord.toUpperCase();
      }

      if (matchedWord[0] === matchedWord[0].toUpperCase()) {
        return targetWord[0].toUpperCase() + targetWord.slice(1);
      }

      return targetWord.toLowerCase();
    });
  });

  return translatedValue;
};

const getAttributeDataKey = (prefix, attributeName) => {
  const normalizedAttributeName = attributeName
    .replace(/-([a-z])/g, (_, character) => character.toUpperCase())
    .replace(/^([a-z])/, (_, character) => character.toUpperCase());

  return `agromart${prefix}${normalizedAttributeName}`;
};

const translateRawText = (value, language) => {
  if (!value || typeof value !== 'string') {
    return value;
  }

  const pairs = language === 'tr' ? EN_TO_TR_PAIRS : TR_TO_EN_PAIRS;
  let translatedValue = value;

  pairs.forEach(([sourceText, targetText]) => {
    if (!sourceText || translatedValue.indexOf(sourceText) === -1) {
      return;
    }
    translatedValue = translatedValue.split(sourceText).join(targetText);
  });

  return applyWordLevelTranslation(translatedValue, language);
};

const shouldSkipElement = (element) => {
  if (!element) {
    return true;
  }

  if (SKIP_TRANSLATION_TAGS.has(element.tagName)) {
    return true;
  }

  return Boolean(element.closest('[data-no-translate="true"]'));
};

const applyTextNodeTranslation = (textNode, language) => {
  if (!textNode || textNode.nodeType !== Node.TEXT_NODE) {
    return;
  }

  const parentElement = textNode.parentElement;
  if (!parentElement || shouldSkipElement(parentElement)) {
    return;
  }

  const currentValue = textNode.nodeValue || '';
  if (!currentValue.trim()) {
    return;
  }

  const originalKey = '__agromartOriginalText';
  const translatedKey = '__agromartTranslatedText';
  const previousOriginalValue = textNode[originalKey];
  const previousTranslatedValue = textNode[translatedKey];

  if (previousOriginalValue === undefined) {
    textNode[originalKey] = currentValue;
  } else if (currentValue !== previousTranslatedValue) {
    textNode[originalKey] = currentValue;
  }

  const translatedValue = translateRawText(textNode[originalKey], language);
  if (currentValue !== translatedValue) {
    textNode.nodeValue = translatedValue;
  }

  textNode[translatedKey] = translatedValue;
};

const applyElementAttributeTranslation = (element, language) => {
  if (!element || !(element instanceof Element) || shouldSkipElement(element)) {
    return;
  }

  TRANSLATABLE_ATTRIBUTES.forEach((attributeName) => {
    if (!element.hasAttribute(attributeName)) {
      return;
    }

    const currentValue = element.getAttribute(attributeName) || '';
    if (!currentValue.trim()) {
      return;
    }

    const originalKey = getAttributeDataKey('Original', attributeName);
    const translatedKey = getAttributeDataKey('Translated', attributeName);
    const previousOriginalValue = element.dataset[originalKey];
    const previousTranslatedValue = element.dataset[translatedKey];

    if (previousOriginalValue === undefined) {
      element.dataset[originalKey] = currentValue;
    } else if (currentValue !== previousTranslatedValue) {
      element.dataset[originalKey] = currentValue;
    }

    const translatedValue = translateRawText(element.dataset[originalKey] || '', language);
    if (currentValue !== translatedValue) {
      element.setAttribute(attributeName, translatedValue);
    }
    element.dataset[translatedKey] = translatedValue;
  });
};

const applySubtreeTranslation = (rootNode, language) => {
  if (!rootNode) {
    return;
  }

  if (rootNode.nodeType === Node.TEXT_NODE) {
    applyTextNodeTranslation(rootNode, language);
    return;
  }

  if (!(rootNode instanceof Element) || shouldSkipElement(rootNode)) {
    return;
  }

  applyElementAttributeTranslation(rootNode, language);

  const treeWalker = document.createTreeWalker(rootNode, NodeFilter.SHOW_TEXT);
  let textNode = treeWalker.nextNode();
  while (textNode) {
    applyTextNodeTranslation(textNode, language);
    textNode = treeWalker.nextNode();
  }

  rootNode
    .querySelectorAll(TRANSLATABLE_ATTRIBUTES.map((attributeName) => `[${attributeName}]`).join(','))
    .forEach((element) => applyElementAttributeTranslation(element, language));
};

const getInitialLanguage = () => {
  if (typeof window === 'undefined') {
    return 'tr';
  }

  const storedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return SUPPORTED_LANGUAGES.includes(storedLanguage) ? storedLanguage : 'tr';
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(getInitialLanguage);

  const t = useCallback(
    (enText, trText) => (language === 'tr' ? trText : enText),
    [language]
  );

  const setLanguage = (nextLanguage) => {
    const normalizedLanguage = SUPPORTED_LANGUAGES.includes(nextLanguage) ? nextLanguage : 'tr';
    setLanguageState(normalizedLanguage);

    if (typeof window !== 'undefined') {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, normalizedLanguage);
    }
  };

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    document.documentElement.lang = language;

    const rootNode = document.getElementById('root');
    if (!rootNode) {
      return undefined;
    }

    applySubtreeTranslation(rootNode, language);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'characterData') {
          applyTextNodeTranslation(mutation.target, language);
          return;
        }

        if (mutation.type === 'attributes') {
          applyElementAttributeTranslation(mutation.target, language);
          return;
        }

        mutation.addedNodes.forEach((node) => applySubtreeTranslation(node, language));
      });
    });

    observer.observe(rootNode, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: TRANSLATABLE_ATTRIBUTES,
    });

    return () => observer.disconnect();
  }, [language]);

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      t
    }),
    [language, t]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }

  return context;
};
